import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, Phone, User, CheckCircle, AlertCircle, Eye, Building, MapPin } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api';

interface Campaign {
  id: string;
  shareToken: string;
  name: string;
  subject: string;
  content: string;
  createdBy: { name: string };
  subscriberCount: number;
  viewCount: number;
  createdAt: string;
}

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  company?: string;
  location?: string;
  source: string;
}

export default function PublicCampaignView() {
  const { shareToken } = useParams<{ shareToken: string }>();
  
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [notification, setNotification] = useState<{ type: string; message: string } | null>(null);
  const [errors, setErrors] = useState<Partial<LeadFormData>>({});

  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    source: 'campaign_share'
  });

  // Fetch campaign
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await fetch(`${API_URL}/subscribers/campaign/${shareToken}`);
        const data = await response.json();
        if (data.success) {
          setCampaign(data.campaign);
        } else {
          showNotification('error', 'Campaign not found');
        }
      } catch (error) {
        showNotification('error', 'Failed to load campaign');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaign();
  }, [shareToken]);

  const showNotification = (type: string, message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LeadFormData> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email required';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Valid phone number required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showNotification('error', 'Please fill all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/subscribers/subscribe/${shareToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subscribedAt: new Date().toISOString(),
          campaignId: campaign?.id,
          ipAddress: 'client-side', // Will be captured by backend
          userAgent: navigator.userAgent
        })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        showNotification('success', '✅ Successfully subscribed!');
        
        // Track conversion (for your analytics)
        console.log('📊 Lead captured:', {
          ...formData,
          campaign: campaign?.name,
          timestamp: new Date().toISOString()
        });
      } else {
        showNotification('error', data.error || 'Failed to subscribe');
      }
    } catch (error) {
      showNotification('error', 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Campaign Not Found</h2>
          <p className="text-gray-600">This link is invalid or expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white z-50 ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{campaign.createdBy?.name || 'Unknown'}</p>
              <p className="font-semibold">{campaign.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Campaign Content */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{campaign.subject}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" /> {campaign.viewCount.toLocaleString()} views
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" /> {campaign.subscriberCount.toLocaleString()} subscribers
                </span>
              </div>
            </div>
            <div className="p-8">
              <div 
                dangerouslySetInnerHTML={{ __html: campaign.content }}
                className="prose max-w-none"
              />
            </div>
          </div>

          {/* Right: Lead Capture Form */}
          <div>
            <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-8">
              {!submitted ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Get Exclusive Access 🎯
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Fill in your details to subscribe and receive updates.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name - Required */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        <User className="w-4 h-4 inline mr-1" /> Full Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="John Doe"
                        className={`w-full px-4 py-3 border rounded-lg ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email - Required */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        <Mail className="w-4 h-4 inline mr-1" /> Email Address *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="you@example.com"
                        className={`w-full px-4 py-3 border rounded-lg ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    {/* Phone - Required */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        <Phone className="w-4 h-4 inline mr-1" /> Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+1 (555) 123-4567"
                        className={`w-full px-4 py-3 border rounded-lg ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>

                    {/* Company - Optional */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        <Building className="w-4 h-4 inline mr-1" /> Company (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        placeholder="Your Company"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Location - Optional */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        <MapPin className="w-4 h-4 inline mr-1" /> Location (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder="City, Country"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent"></div>
                          Subscribing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          Subscribe Now
                        </span>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      ✓ We respect your privacy ✓ Unsubscribe anytime ✓ No spam
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Thank You! 🎉</h3>
                  <p className="text-gray-600">You've been added to our list.</p>
                  <p className="text-sm text-gray-500 mt-4">Check your email for confirmation.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}